import { css } from "@emotion/css";
import {
  Text,
  Stack,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Separator,
  Link,
  ProgressIndicator,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IColumn,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  TextField,
  Panel,
  PanelType,
  Pivot,
  PivotItem
} from "@fluentui/react";
import {
  ActionsContext,
  ActionTitle,
  fyFp,
  LoadingStatus,
  LocalMessage,
  OpsDashboardApi,
  UserContext,
  AuthorizationToken
} from "@jem/components";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  CalendarRequestInfo,
  CalendarRequestDetail,
  CalendarUploadResult,
  approveCalendarRequest,
  getCalendarRequests,
  getCalendarRequestDetail,
  getCalendarSqlPreview,
  getCalendarTemplateUrl,
  rejectCalendarRequest,
  uploadCalendarFile
} from "./CalendarUpload.requests";

export interface CalendarUploadPageProps {
  configuration: OpsDashboardApi;
}

const fiscalYearOptions: IDropdownOption[] = Array.from({ length: 10 }, (_, i) => {
  const fy = fyFp.fiscalYear - 2 + i;
  return { key: `${fy}`, text: `FY${fy}` };
});

const CalendarUploadPage: React.FC<CalendarUploadPageProps> = (props) => {
  const { styles } = useContext(ActionsContext);
  const userContext = useContext(UserContext);

  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number>(fyFp.fiscalYear + 1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<LoadingStatus>(LoadingStatus.Idle);
  const [message, setMessage] = useState<LocalMessage | null>(null);
  const [uploadResult, setUploadResult] = useState<CalendarUploadResult | null>(null);
  const [requests, setRequests] = useState<CalendarRequestInfo[]>([]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectRequestId, setRejectRequestId] = useState("");
  const [rejectComments, setRejectComments] = useState("");
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [detailRequest, setDetailRequest] = useState<CalendarRequestDetail | null>(null);
  const [detailSql, setDetailSql] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setMessage(null);
      setUploadResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"]
    },
    maxFiles: 1
  });

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage({ message: "Please select a file to upload.", type: MessageBarType.warning });
      return;
    }

    setUploadStatus(LoadingStatus.Pending);
    setMessage(null);
    setUploadResult(null);

    try {
      const result = await uploadCalendarFile(props.configuration, userContext.accessToken, selectedFile);

      if (result && result.success) {
        setMessage({ message: result.message, type: MessageBarType.success });
        setUploadResult(result);
        setSelectedFile(null);
        await loadRequests();
      } else if (result) {
        setMessage({
          message: `${result.message} ${result.errors?.join("; ") || ""}`,
          type: MessageBarType.error
        });
        setUploadResult(result);
      } else {
        setMessage({ message: "Upload failed. Please try again.", type: MessageBarType.error });
      }
    } catch (err: any) {
      setMessage({ message: `Upload failed: ${err.message || "Unknown error"}`, type: MessageBarType.error });
    } finally {
      setUploadStatus(LoadingStatus.Resolved);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const url = getCalendarTemplateUrl(props.configuration, selectedFiscalYear);
      const token = await AuthorizationToken(userContext.accessToken);

      const resp = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: token,
          accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        },
        mode: "cors",
        referrerPolicy: "strict-origin-when-cross-origin"
      });

      if (!resp.ok) {
        throw new Error(`Download failed: ${resp.statusText}`);
      }

      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `JEM_Calendar_Template_FY${selectedFiscalYear}.xlsx`);
      document.body.appendChild(link);
      link.dispatchEvent(new MouseEvent("click"));
      URL.revokeObjectURL(blobUrl);
      if (link.parentNode) link.parentNode.removeChild(link);

      setMessage({ message: "Template downloaded successfully.", type: MessageBarType.success });
    } catch (err: any) {
      setMessage({ message: `Template download failed: ${err.message}`, type: MessageBarType.error });
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setMessage(null);
    setUploadResult(null);
  };

  const loadRequests = async () => {
    const data = await getCalendarRequests(props.configuration, userContext.accessToken);
    if (data) setRequests(data);
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleApprove = async (requestId: string) => {
    try {
      const result = await approveCalendarRequest(props.configuration, userContext.accessToken, requestId);
      if (result && result.success) {
        setMessage({ message: result.message, type: MessageBarType.success });
      } else {
        setMessage({ message: result?.message || "Approval failed.", type: MessageBarType.error });
      }
      await loadRequests();
    } catch (err: any) {
      setMessage({ message: `Approval failed: ${err.message}`, type: MessageBarType.error });
    }
  };

  const handleRejectConfirm = async () => {
    try {
      const result = await rejectCalendarRequest(props.configuration, userContext.accessToken, rejectRequestId, rejectComments);
      if (result) {
        setMessage({ message: "Request rejected.", type: MessageBarType.success });
        setRejectDialogOpen(false);
        setRejectComments("");
        await loadRequests();
      } else {
        setMessage({ message: "Rejection failed: No response from server.", type: MessageBarType.error });
      }
    } catch (err: any) {
      setMessage({ message: `Rejection failed: ${err.message}`, type: MessageBarType.error });
    }
  };

  const handleViewDetail = async (requestId: string) => {
    setDetailLoading(true);
    setDetailPanelOpen(true);
    setDetailRequest(null);
    setDetailSql(null);

    try {
      const [detail, sqlPreview] = await Promise.all([
        getCalendarRequestDetail(props.configuration, userContext.accessToken, requestId),
        getCalendarSqlPreview(props.configuration, userContext.accessToken, requestId)
      ]);
      setDetailRequest(detail);
      setDetailSql(sqlPreview?.sql || null);
    } catch (err: any) {
      setMessage({ message: `Failed to load details: ${err.message}`, type: MessageBarType.error });
    } finally {
      setDetailLoading(false);
    }
  };

  const requestColumns: IColumn[] = [
    { key: "fy", name: "FY", fieldName: "fiscalYear", minWidth: 50, maxWidth: 60 },
    { key: "status", name: "Status", fieldName: "status", minWidth: 80, maxWidth: 100 },
    { key: "createdBy", name: "Submitted By", fieldName: "createdBy", minWidth: 80, maxWidth: 120 },
    { key: "createdDate", name: "Submitted", fieldName: "createdDate", minWidth: 120, maxWidth: 160 },
    { key: "approvedBy", name: "Approved By", fieldName: "approvedBy", minWidth: 100, maxWidth: 160 },
    { key: "jemCount", name: "JEM Events", fieldName: "jemEventCount", minWidth: 60, maxWidth: 80 },
    { key: "ihccCount", name: "IHCC Events", fieldName: "ihccEventCount", minWidth: 60, maxWidth: 80 },
    {
      key: "actions",
      name: "Actions",
      minWidth: 120,
      maxWidth: 150,
      onRender: (item: CalendarRequestInfo) => {
        return (
          <Stack horizontal tokens={{ childrenGap: 4 }}>
            <IconButton
              iconProps={{ iconName: "View" }}
              title="View Details & SQL"
              onClick={() => handleViewDetail(item.requestId)}
            />
            {(item.status === "Submitted" || item.status === "In Review") && (
              <>
                <IconButton
                  iconProps={{ iconName: "Accept" }}
                  title="Approve"
                  onClick={() => handleApprove(item.requestId)}
                />
                <IconButton
                  iconProps={{ iconName: "Cancel" }}
                  title="Reject"
                  onClick={() => {
                    setRejectRequestId(item.requestId);
                    setRejectDialogOpen(true);
                  }}
                />
              </>
            )}
          </Stack>
        );
      }
    }
  ];

  return (
    <div className={styles.page}>
      <ActionTitle title="Fiscal Calendar Upload" />
      <Separator />

      <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: 700, padding: "16px" } }}>
        {/* Fiscal Year Selection + Template Download */}
        <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="end">
          <Dropdown
            label="Fiscal Year"
            selectedKey={`${selectedFiscalYear}`}
            options={fiscalYearOptions}
            onChange={(_, option) => {
              if (option) setSelectedFiscalYear(Number(option.text.replace("FY", "")));
            }}
            styles={{ dropdown: { width: 120 } }}
          />
          <Link onClick={handleDownloadTemplate}>Download Template (FY{selectedFiscalYear})</Link>
        </Stack>

        {/* Instructions */}
        <MessageBar messageBarType={MessageBarType.info} isMultiline>
          <Text variant="small">
            1. Download the template for the target fiscal year.
            <br />
            2. Fill in the Date column for each event on both JEM and IHCC sheets.
            <br />
            3. Upload the completed file below and click Submit.
          </Text>
        </MessageBar>

        {/* File Drop Zone */}
        <div
          {...getRootProps()}
          className={css({
            border: `2px dashed ${isDragActive ? "#0078d4" : "#c8c8c8"}`,
            borderRadius: "4px",
            padding: "24px",
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: isDragActive ? "#f3f2f1" : "#faf9f8",
            transition: "all 0.2s ease"
          })}
        >
          <input {...getInputProps()} />
          {selectedFile ? (
            <Stack tokens={{ childrenGap: 4 }}>
              <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
                {selectedFile.name}
              </Text>
              <Text variant="small" styles={{ root: { color: "#666" } }}>
                {(selectedFile.size / 1024).toFixed(1)} KB
              </Text>
            </Stack>
          ) : (
            <Text variant="medium" styles={{ root: { color: "#666" } }}>
              Drag and drop a .xlsx file here, or click to browse
            </Text>
          )}
        </div>

        {/* Action Buttons */}
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <PrimaryButton
            text="Submit"
            onClick={handleUpload}
            disabled={!selectedFile || uploadStatus === LoadingStatus.Pending}
          />
          <DefaultButton text="Clear" onClick={handleClear} disabled={uploadStatus === LoadingStatus.Pending} />
        </Stack>

        {/* Loading Indicator */}
        {uploadStatus === LoadingStatus.Pending && <ProgressIndicator label="Uploading and processing calendar..." />}

        {/* Status Message */}
        {message && (
          <MessageBar
            messageBarType={message.type}
            isMultiline
            onDismiss={() => setMessage(null)}
            dismissButtonAriaLabel="Close"
          >
            {message.message}
          </MessageBar>
        )}

        {/* Upload Result Summary */}
        {uploadResult && uploadResult.success && uploadResult.summary && (
          <Stack
            tokens={{ childrenGap: 4 }}
            styles={{
              root: { padding: "12px", backgroundColor: "#f3f2f1", borderRadius: "4px" }
            }}
          >
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Upload Summary
            </Text>
            <Text variant="small">JEM events processed: {uploadResult.summary.jemEventsInserted}</Text>
            <Text variant="small">
              IHCC events processed (jem_data): {uploadResult.summary.ihccJemDataInserted}
            </Text>
            <Text variant="small">
              IHCC events processed (PaymentOrders): {uploadResult.summary.ihccPaymentOrdersInserted}
            </Text>
            <Text variant="small">
              JEM Fiscal Month created: {uploadResult.summary.jemFiscalMonthCreated ? "Yes" : "Already exists"}
            </Text>
            <Text variant="small">
              CALNDR02 (jem_data): {uploadResult.summary.calndr02FiscalMonthJemDataCreated ? "Yes" : "Already exists"}
            </Text>
            <Text variant="small">
              CALNDR02 (MasterData):{" "}
              {uploadResult.summary.calndr02FiscalMonthMasterDataCreated ? "Yes" : "Already exists"}
            </Text>
          </Stack>
        )}

        {/* Error Details */}
        {uploadResult && !uploadResult.success && uploadResult.errors && uploadResult.errors.length > 0 && (
          <Stack tokens={{ childrenGap: 4 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: "#a80000" } }}>
              Errors
            </Text>
            {uploadResult.errors.map((err, idx) => (
              <Text key={idx} variant="small" styles={{ root: { color: "#a80000" } }}>
                • {err}
              </Text>
            ))}
          </Stack>
        )}

        {/* Calendar Requests Grid */}
        <Separator />
        <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
          Calendar Upload Requests
        </Text>
        {requests.length > 0 ? (
          <DetailsList
            items={requests}
            columns={requestColumns}
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
            isHeaderVisible={true}
          />
        ) : (
          <Text variant="small" styles={{ root: { color: "#666" } }}>
            No calendar upload requests found.
          </Text>
        )}
      </Stack>

      {/* Reject Dialog */}
      <Dialog
        hidden={!rejectDialogOpen}
        onDismiss={() => setRejectDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Reject Calendar Request",
          subText: "Please provide a reason for rejection."
        }}
      >
        <TextField
          label="Comments"
          multiline
          rows={3}
          value={rejectComments}
          onChange={(_, val) => setRejectComments(val || "")}
        />
        <DialogFooter>
          <PrimaryButton text="Reject" onClick={handleRejectConfirm} />
          <DefaultButton text="Cancel" onClick={() => setRejectDialogOpen(false)} />
        </DialogFooter>
      </Dialog>

      {/* Detail Panel - shows parsed events and SQL preview */}
      <Panel
        isOpen={detailPanelOpen}
        onDismiss={() => setDetailPanelOpen(false)}
        type={PanelType.large}
        headerText={detailRequest ? `Calendar FY${detailRequest.fiscalYear} - Review` : "Calendar Request Detail"}
      >
        {detailLoading && <ProgressIndicator label="Loading details..." />}
        {!detailLoading && detailRequest && (
          <Pivot>
            <PivotItem headerText={`JEM Events (${detailRequest.jemEvents?.length || 0})`}>
              <DetailsList
                items={detailRequest.jemEvents || []}
                columns={[
                  { key: "fm", name: "Fiscal Month", fieldName: "fiscalMonth", minWidth: 80, maxWidth: 100 },
                  { key: "eid", name: "Event ID", fieldName: "eventId", minWidth: 60, maxWidth: 80 },
                  { key: "ename", name: "Event Name", fieldName: "eventName", minWidth: 200, maxWidth: 350 },
                  {
                    key: "date",
                    name: "Date",
                    fieldName: "date",
                    minWidth: 100,
                    maxWidth: 140,
                    onRender: (item: any) => <span>{new Date(item.date).toLocaleDateString()}</span>
                  }
                ]}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                isHeaderVisible={true}
              />
            </PivotItem>
            <PivotItem headerText={`IHCC Events (${detailRequest.ihccEvents?.length || 0})`}>
              <DetailsList
                items={detailRequest.ihccEvents || []}
                columns={[
                  { key: "fm", name: "Fiscal Month", fieldName: "fiscalMonth", minWidth: 80, maxWidth: 100 },
                  { key: "eid", name: "Event ID", fieldName: "eventId", minWidth: 60, maxWidth: 80 },
                  { key: "ename", name: "Event Name", fieldName: "eventName", minWidth: 200, maxWidth: 350 },
                  {
                    key: "date",
                    name: "Date",
                    fieldName: "date",
                    minWidth: 100,
                    maxWidth: 140,
                    onRender: (item: any) => <span>{new Date(item.date).toLocaleDateString()}</span>
                  }
                ]}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                isHeaderVisible={true}
              />
            </PivotItem>
            <PivotItem headerText="SQL Preview">
              {detailSql ? (
                <pre
                  className={css({
                    backgroundColor: "#1e1e1e",
                    color: "#d4d4d4",
                    padding: "16px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    overflow: "auto",
                    maxHeight: "600px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "Consolas, 'Courier New', monospace"
                  })}
                >
                  {detailSql}
                </pre>
              ) : (
                <Text>No SQL preview available.</Text>
              )}
            </PivotItem>
          </Pivot>
        )}
      </Panel>
    </div>
  );
};

export default CalendarUploadPage;
